import Link from 'next/link';
import Image from 'next/image';
import { Product } from '@/lib/shopify/types';
import { formatPrice } from '@/lib/shopify';

export default function ProductCard({ product }: { product: Product }) {
  const img = product.featuredImage || product.images[0];
  const price = product.priceRange.minVariantPrice;
  const firstVariant = product.variants[0];
  const compareAt = firstVariant?.compareAtPrice;
  const onSale =
    compareAt && parseFloat(compareAt.amount) > parseFloat(price.amount);

  return (
    <Link href={`/products/${product.handle}`} className="pcard">
      <div className="pcard-image">
        {img?.url ? (
          <Image
            src={img.url}
            alt={img.altText || product.title}
            width={400}
            height={400}
            className="pcard-img"
          />
        ) : (
          <div className="pcard-img-ph" />
        )}
        {onSale && <span className="pcard-badge">Sale</span>}
      </div>
      <div className="pcard-body">
        {product.vendor && <p className="pcard-vendor">{product.vendor}</p>}
        <h3 className="pcard-title">{product.title}</h3>
        <div className="pcard-price-row">
          <span className="pcard-price">{formatPrice(price)}</span>
          {onSale && (
            <span className="pcard-compare">{formatPrice(compareAt!)}</span>
          )}
        </div>
      </div>
      <style>{`
        .pcard {
          display: flex;
          flex-direction: column;
          background: var(--color-white);
          border: 1px solid var(--color-border);
          border-radius: 0.75rem 0.75rem 0 0;
          overflow: hidden;
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .pcard:hover {
          box-shadow: 0 4px 16px rgba(0,0,0,0.1);
        }
        .pcard-image {
          position: relative;
          aspect-ratio: 1;
          background: transparent;
          overflow: hidden;
        }
        .pcard-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 0.5rem;
          box-sizing: border-box;
          transition: transform 0.4s ease;
        }
        .pcard:hover .pcard-img { transform: scale(1.05); }
        .pcard-img-ph { width: 100%; height: 100%; background: var(--color-off-white); }
        .pcard-badge {
          position: absolute;
          top: 0.5rem;
          left: 0.5rem;
          background: #cf3f43;
          color: var(--color-white);
          font-size: 0.7rem;
          font-weight: 700;
          padding: 0.2rem 0.55rem;
          border-radius: 0.25rem;
          letter-spacing: 0.04em;
        }
        .pcard-body {
          padding: 0.75rem;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .pcard-vendor {
          font-size: 0.65rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--color-text-muted);
          margin: 0;
        }
        .pcard-title {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--color-text-primary);
          line-height: 1.35;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          margin: 0;
          min-height: 2.3em;
        }
        .pcard-price-row {
          display: flex;
          align-items: baseline;
          gap: 0.5rem;
          margin-top: 0.25rem;
        }
        .pcard-price {
          font-size: 0.95rem;
          font-weight: 700;
          color: var(--color-primary);
        }
        .pcard-compare {
          font-size: 0.8rem;
          color: var(--color-text-muted);
          text-decoration: line-through;
        }
      `}</style>
    </Link>
  );
}
